import { BrowserRouter, Navigate, useRoutes, useLocation } from 'react-router-dom'
import { useMemo, useEffect, useState } from 'react'
import { useAuthStore } from '@/store/auth'
import { generateRoutesFromUserRoutes } from '@/routes/generateRoutes'
import { HelmetProvider } from 'react-helmet-async'
import { Providers } from '@/components/providers/Providers'
import { SessionProvider } from '@/components/providers/SessionProvider'
import type { RouteObject } from 'react-router-dom'

// Public routes (no authentication required)
import Login from '@/pages/Login'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import TwoFactor from '@/pages/TwoFactor'
import TwoFactorSetup from '@/pages/TwoFactor/Setup'
import Dashboard from '@/pages/Dashboard'
import ClientCheck from '@/pages/ClientCheck'
import CreatePassword from '@/pages/CreatePassword'
import { AuthGuard } from '@/components/auth/AuthGuard'

const BASE_PUBLIC_ROUTES: RouteObject[] = [
  { path: '/client-check', element: <ClientCheck /> },
  { path: '/create-password', element: <CreatePassword /> },
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/two-factor', element: <TwoFactor /> },
  { path: '/two-factor/setup', element: <TwoFactorSetup /> },
]

// Default authenticated routes (fallback when user_routes not loaded yet)
const DEFAULT_AUTH_ROUTES: RouteObject[] = [
  ...BASE_PUBLIC_ROUTES,
  {
    path: '/dashboard',
    element: (
      <AuthGuard>
        <Dashboard />
      </AuthGuard>
    ),
  },
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  // Don't include catch-all here - let the actual route generation handle it
  // This prevents redirecting to dashboard when reloading on other pages
]

const UNAUTH_ROUTES: RouteObject[] = [
  ...BASE_PUBLIC_ROUTES,
  { path: '/', element: <Navigate to="/client-check" replace /> },
  { path: '*', element: <Navigate to="/client-check" replace /> },
]

function AppRoutes({ routes, isInitialized, setIsInitialized }: { routes: RouteObject[]; isInitialized: boolean; setIsInitialized: (value: boolean) => void }) {
  const element = useRoutes(routes)
  const location = useLocation()
  const isLoading = useAuthStore((state) => state.isLoading)
  const token = useAuthStore((state) => state.token)
  const initializeAuth = useAuthStore((state) => state.initializeAuth)

  // Initialize auth on mount
  useEffect(() => {
    let isMounted = true
    const init = async () => {
      try {
        await initializeAuth()
        if (isMounted) {
          setIsInitialized(true)
          if (import.meta.env.DEV) {
            console.log('[App] Auth initialization complete')
          }
        }
      } catch (error) {
        console.error('[App] Auth initialization failed:', error)
        if (isMounted) {
          setIsInitialized(true)
        }
      }
    }
    init()
    return () => {
      isMounted = false
    }
  }, [initializeAuth, setIsInitialized])

  // Show loading overlay only if we're initializing and don't have a token yet
  // Don't show full-page loader on login/2FA pages - let buttons handle loading state
  const isAuthPage = ['/client-check', '/create-password', '/login', '/two-factor', '/two-factor/setup'].includes(location.pathname)
  const shouldShowLoader = (!isInitialized || isLoading) && !token && !isAuthPage

  if (shouldShowLoader) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    )
  }

  return element
}

function App() {
  const user = useAuthStore((state) => state.user)
  const isLoading = useAuthStore((state) => state.isLoading)
  const token = useAuthStore((state) => state.token)
  const [isInitialized, setIsInitialized] = useState(false)

  // Memoize routes to prevent unnecessary regenerations
  // Wait for initialization to complete before generating routes
  const routes = useMemo(() => {
    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('[App] Generating routes:', {
        isInitialized,
        isLoading,
        hasToken: !!token,
        hasUser: !!user,
        hasUserRoutes: user?.user_routes !== undefined,
        userRoutesLength: user?.user_routes?.length || 0
      })
    }

    // Show public routes while initializing (but don't show loading screen if we have token)
    if (!isInitialized) {
      // If we have a token in localStorage, show default auth routes to prevent redirect loop
      if (token) {
        if (import.meta.env.DEV) {
          console.log('[App] Using DEFAULT_AUTH_ROUTES (not initialized, but has token)')
        }
        return DEFAULT_AUTH_ROUTES
      }
      if (import.meta.env.DEV) {
        console.log('[App] Using BASE_PUBLIC_ROUTES (not initialized, no token)')
      }
      return BASE_PUBLIC_ROUTES
    }

    // If we have a token or user, try to generate authenticated routes
    if (token || user) {
      // Get routes from user object, with fallback to localStorage if user_routes is undefined
      let routesToUse: any[] = []

      if (user?.user_routes !== undefined) {
        // Use routes from user object (guaranteed to be an array from auth store)
        routesToUse = Array.isArray(user.user_routes) ? user.user_routes : []
      } else if (token && typeof window !== 'undefined') {
        // Fallback: try to get routes from localStorage during initialization
        try {
          const authStorage = localStorage.getItem('auth-storage')
          if (authStorage) {
            const parsed = JSON.parse(authStorage)
            const storedUser = parsed.state?.user
            if (storedUser?.user_routes && Array.isArray(storedUser.user_routes)) {
              routesToUse = storedUser.user_routes
              if (import.meta.env.DEV) {
                console.log('[App] Using routes from localStorage (user_routes not yet loaded):', {
                  routeCount: routesToUse.length
                })
              }
            }
          }
        } catch (error) {
          console.error('[App] Error reading routes from localStorage:', error)
        }
      }

      // Generate routes (even if empty array - will still include dashboard)
      const generatedRoutes = generateRoutesFromUserRoutes(
        routesToUse,
        BASE_PUBLIC_ROUTES
      )

      if (import.meta.env.DEV) {
        console.log('[App] Generated routes:', {
          userRoutesCount: routesToUse.length,
          generatedRoutesCount: generatedRoutes.length,
          routePaths: generatedRoutes.map(r => r.path),
          hasUser: !!user,
          userRoutesDefined: user?.user_routes !== undefined,
          source: user?.user_routes !== undefined ? 'user' : 'localStorage'
        })
      }

      return generatedRoutes
    }

    // No token or user, show unauthenticated routes
    if (import.meta.env.DEV) {
      console.log('[App] Using UNAUTH_ROUTES (no token or user)')
    }
    return UNAUTH_ROUTES
  }, [user?.user_routes, token, user, isInitialized, isLoading])

  return (
    <HelmetProvider>
      <Providers>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <SessionProvider>
            <AppRoutes routes={routes} isInitialized={isInitialized} setIsInitialized={setIsInitialized} />
          </SessionProvider>
        </BrowserRouter>
      </Providers>
    </HelmetProvider>
  )
}

export default App

